import { onValue, ref, update, off, remove, get } from "firebase/database";
import { db } from "@/firebase/firebase";
import { getPartners } from "@/store/externalSelectors";
import { StoreService } from "./StoreService";

class RealtimeDatabaseService {
  updateUser(userId: string, data: any) {
    const userRef = ref(db, `users/${userId}`);
    update(userRef, data);
  }

  updateCurrentUser(data: any) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    this.updateUser(activeUser.id, data);
  }

  updateCurrentUserBehaviorData(data: any) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const behaviorRef = ref(db, `users/${activeUser.id}/behavior`);
    update(behaviorRef, data);
  }

  updateCurrentUserUiStateData(data: any) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const uiStateRef = ref(db, `users/${activeUser.id}/uiState`);
    update(uiStateRef, data);
  }

  subscribeToUser(
    userId: string,
    callback: (userData: any) => void,
  ): () => void {
    this.unsubscribeFromUser(userId);
    const userRef = ref(db, `users/${userId}`);
    off(userRef, "value");

    return onValue(
      userRef,
      (snapshot) => {
        const userData = snapshot.val();
        callback(userData);
      },
      (error) => {
        console.error("Error subscribing to user", error);
      },
    );
  }

  subscribeToUserVideoData(
    userId: string,
    callback: (videoData: any) => void,
  ): () => void {
    const videoDataRef = ref(db, `videoDataByUser/${userId}`);
    off(videoDataRef, "value");

    return onValue(
      videoDataRef,
      (snapshot) => {
        const videoData = snapshot.val();
        callback(videoData);
      },
      (error) => {
        console.error("Error subscribing to user video data", error);
      },
    );
  }

  subscribeToUserVideoReactions(
    userId: string,
    callback: (videoReactions: any) => void,
  ): () => void {
    const videoReactionsRef = ref(db, `users/${userId}/videoReactions`);
    off(videoReactionsRef, "value");

    return onValue(
      videoReactionsRef,
      (snapshot) => {
        const videoReactions = snapshot.val();
        callback(videoReactions);
      },
      (error) => {
        console.error("Error subscribing to user video reactions", error);
      },
    );
  }

  // subscribeToUserAdvizerData(
  //   userId: string,
  //   callback: (advizerData: any) => void,
  // ): () => void {
  //   const advizerDataRef = ref(db, `users/${userId}/advizerData`);
  //   off(advizerDataRef, "value");
  //   return onValue(
  //     advizerDataRef,
  //     (snapshot) => {
  //       const advizerData = snapshot.val();
  //       callback(advizerData);
  //     },
  //     (error) => {
  //       console.error("Error subscribing to user advizer data", error);
  //     },
  //   );
  // }

  unsubscribeFromUser(userId: string) {
    const userRef = ref(db, `users/${userId}`);
    off(userRef, "value");
  }

  updateCurrentUserPartner(userId: string, partnerId: string) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const partner = getPartners()?.find((partner) => partner.id === partnerId);

    if (partner) {
      this.updateUser(userId, {
        partnerId,
        partnerSlug: partner?.slug || partner?.title,
      });
    }
  }

  updateUserPlaylistTimestamps(params: { playlistId: string }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const playlistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/playlists/${params.playlistId}`,
    );
    update(playlistsRef, {
      lastUpdatedAt: Date.now(),
    });
  }

  updateCurrentUserPlaylistStepData(params: {
    playlistId: string;
    stepKey: string;
    data: any;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    console.log("Updating current user playlist step data", {
      activeUser,
      params,
    });

    const playlistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/playlists/${params.playlistId}/steps/${params.stepKey}`,
    );
    update(playlistsRef, params.data);

    this.updateUserPlaylistTimestamps({ playlistId: params.playlistId });
  }

  addCurrentUserSubject({
    subjectId,
    subjectData,
  }: {
    subjectId: string;
    subjectData: any;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const subjectsRef = ref(
      db,
      `users/${activeUser.id}/profileData/subjects/${subjectId}`,
    );
    update(subjectsRef, subjectData);
  }

  removeCurrentUserSubject({ subjectId }: { subjectId: string }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const subjectsRef = ref(
      db,
      `users/${activeUser.id}/profileData/subjects/${subjectId}`,
    );
    remove(subjectsRef);
  }

  clearCurrentUserSubjects() {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const subjectsRef = ref(db, `users/${activeUser.id}/profileData/subjects`);
    remove(subjectsRef);
  }

  clearCurrentUserIndustries() {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const industriesRef = ref(
      db,
      `users/${activeUser.id}/profileData/industries`,
    );
    remove(industriesRef);
  }

  addCurrentUserIndustry({
    industryId,
    industryData,
  }: {
    industryId: string;
    industryData: any;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const industriesRef = ref(
      db,
      `users/${activeUser.id}/profileData/industries/${industryId}`,
    );
    update(industriesRef, industryData);
  }

  removeCurrentUserIndustry({ industryId }: { industryId: string }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const industriesRef = ref(
      db,
      `users/${activeUser.id}/profileData/industries/${industryId}`,
    );
    remove(industriesRef);
  }

  updateCurrentUserVideoReaction({
    vimeoId,
    reaction,
    questionNumber,
    advizerId,
  }: {
    vimeoId: string;
    reaction: string;
    questionNumber: number;
    advizerId: string;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const videoReactionRef = ref(
      db,
      `users/${activeUser.id}/videoReactions/${vimeoId}`,
    );
    update(videoReactionRef, {
      reaction,
      timestamp: Date.now(),
      questionNumber,
      advizerId,
    });
  }

  updateCurrentUserRecommendedPlaylists({
    playlistIds,
  }: {
    playlistIds: string[];
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const recommendedPlaylistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/recommendedPlaylists`,
    );
    update(
      recommendedPlaylistsRef,
      playlistIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as any),
    );
  }

  clearUserRecommendedPlaylists() {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const recommendedPlaylistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/recommendedPlaylists`,
    );
    remove(recommendedPlaylistsRef);
  }

  async updateUserVideoData(params: {
    vimeoId: string;
    data: {
      secondsWatched?: number;
      totalSecondsWatched?: number;
      percentWatched?: number;
    };
    secondsToAdd?: number;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const originalVideoData = (
      await get(
        ref(db, `videoDataByUser/${activeUser.id}/videos/${params.vimeoId}`),
      )
    ).val();

    const newTotalSecondsWatched =
      (originalVideoData?.totalSecondsWatched || 0) +
      (params.secondsToAdd || 0);

    const videoWatchingDataRef = ref(
      db,
      `videoDataByUser/${activeUser.id}/videos/${params.vimeoId}`,
    );

    update(videoWatchingDataRef, {
      ...params.data,
      lastUpdatedAt: Date.now(),
      totalSecondsWatched: Math.max(
        newTotalSecondsWatched,
        params.data?.secondsWatched || 0,
        originalVideoData?.totalSecondsWatched || 0,
        0,
      ),
    });
  }

  async updateUserVideoTotalSecondsWatched(params: {
    vimeoId: string;
    secondsToAdd: number;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const originalVideoData = (
      await get(
        ref(db, `videoDataByUser/${activeUser.id}/videos/${params.vimeoId}`),
      )
    ).val();

    console.log({ originalVideoData });

    // const videoWatchingDataRef = ref(
    //   db,
    //   `videoDataByUser/${activeUser.id}/videos/${params.vimeoId}`,
    // );
    // update(videoWatchingDataRef, {
    //   ...originalVideoData,
    //   totalSecondsWatched: params.totalSecondsWatched,
    // });
  }

  markPlaylistAsCompleted(params: { playlistId: string }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const playlistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/playlists/${params.playlistId}`,
    );
    update(playlistsRef, { completed: Date.now() });
  }

  async fetchPlaylistCompletionStatus(params: { playlistId: string }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const playlistsRef = ref(
      db,
      `users/${activeUser.id}/playlistData/playlists/${params.playlistId}/completed`,
    );
    const snapshot = await get(playlistsRef);
    return snapshot.val() || null;
  }

  updateCurrentUserRecommendedAdvizers({
    advizerIds,
  }: {
    advizerIds: string[];
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const recommendedAdvizersRef = ref(
      db,
      `users/${activeUser.id}/advizerData/recommendedAdvizers`,
    );
    update(
      recommendedAdvizersRef,
      advizerIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as any),
    );
  }

  clearUserRecommendedAdvizers() {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const recommendedAdvizersRef = ref(
      db,
      `users/${activeUser.id}/advizerData/recommendedAdvizers`,
    );
    remove(recommendedAdvizersRef);
  }

  setIsRequestionAdvizerRecs(isRequesting: boolean) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const isRequestionAdvizerRecsRef = ref(
      db,
      `users/${activeUser.id}/advizerData`,
    );

    update(isRequestionAdvizerRecsRef, {
      isRequestingAdvizerRecs: isRequesting,
    });
  }

  setIsRequestionPlaylistRecs(isRequesting: boolean) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const isRequestionPlaylistRecsRef = ref(
      db,
      `users/${activeUser.id}/playlistData`,
    );

    update(isRequestionPlaylistRecsRef, {
      isRequestingPlaylistRecs: isRequesting,
    });
  }
}

export const RealtimeDatabase = new RealtimeDatabaseService();
